"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  useHydratedScroll,
  useHydratedParallax,
} from "@/hooks/useHydratedScroll";
import { getImageUrl } from "@/utils/utils";

interface HomePageSustainabilityClientProps {
  lang: string;
  homepageContent: any;
}

export default function HomePageSustainabilityClient({
  lang,
  homepageContent,
}: HomePageSustainabilityClientProps) {
  const [isMobile, setIsMobile] = useState(false);
  const sustainabilityRef = useRef<HTMLElement>(null);

  // State to track if component is mounted (prevents hydration errors)
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Hydration-safe scroll hooks
  const {
    scrollYProgress: sustainabilityScrollProgress,
    isMounted: sustainabilityMounted,
  } = useHydratedScroll({
    target: sustainabilityRef,
    offset: ["start end", "end start"],
  });

  const sustainabilityBackgroundY = useHydratedParallax(
    sustainabilityScrollProgress,
    ["-20%", "20%"],
    sustainabilityMounted
  );

  // Mobile detection for optimized animations
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);

    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  // Animation variants - exact match from original
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: isMobile ? 30 : 60, // Reduced movement on mobile
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: isMobile ? 0.6 : 0.8, // Faster animation on mobile
        ease: "easeOut",
      },
    },
  };

  return (
    <section ref={sustainabilityRef} className="relative">
      {/* Parallax Background Image with Overlay */}
      <div className="relative h-[400px] sm:h-[500px] lg:h-[600px] overflow-hidden">
        <motion.div
          className="absolute w-full h-[120%] -top-[10%] sm:h-[130%] sm:-top-[15%] lg:h-[120%] lg:-top-[10%]"
          style={{
            y: sustainabilityMounted ? sustainabilityBackgroundY : 0,
          }}
        >
          {homepageContent?.sustainability?.backgroundImage && (
            <Image
              src={getImageUrl(
                homepageContent?.sustainability?.backgroundImage?.url || ""
              )}
              alt={
                homepageContent?.sustainability?.backgroundImage?.name || ""
              }
              fill
              className="object-cover object-center"
              priority
            />
          )}
        </motion.div>
        {/* Overlay for text readability - positioned between image and text */}
        <div className="absolute inset-0 opacity-80 bg-[#1A1D21] z-10"></div>

        {/* Header Content */}
        <div className="absolute inset-0 flex items-start z-20">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-[61px] w-full mt-[60px] sm:mt-[80px] lg:mt-[100px]">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-6 sm:gap-8 lg:gap-0 w-full">
              {/* Header Text Container */}
              <div className="flex flex-col gap-4 max-w-full lg:max-w-[771px]">
                {/* Header Icon and Title */}
                <motion.div
                  className="flex items-center gap-[10px]"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="w-[20px] sm:w-[30px] h-[1px] bg-white"></div>
                  <span className="text-white font-medium text-sm sm:text-[16px] leading-[16px] capitalize">
                    {homepageContent?.sustainability?.title || "Sustainability"}
                  </span>
                </motion.div>

                {/* Header Subtitle */}
                <motion.h2
                  className="text-white text-2xl sm:text-3xl lg:text-[48px] font-medium leading-tight lg:leading-[58px] tracking-[-0.5px] lg:tracking-[-1.44px]"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  {homepageContent?.sustainability?.subtitle ||
                    "Contributing to solving social issues through business activities"}
                </motion.h2>
              </div>

              {/* See More Button */}
              <Link href={`/${lang}/sustainability`}>
                <motion.button
                  className="group inline-flex items-center gap-2 lg:gap-[10px] px-4 sm:px-6 py-3 lg:py-[17px] rounded-[30px] bg-primary-800 text-white hover:bg-white hover:text-primary-800 transition-all duration-300 self-start lg:self-auto mt-4 sm:mt-6 lg:mt-0 cursor-pointer"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <svg
                    width="8"
                    height="14"
                    viewBox="0 0 8 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-2 h-3 lg:w-2 lg:h-3.5 transition-colors duration-300"
                  >
                    <path
                      d="M6.91793 7.37499L2.0406 12.2107C1.8326 12.4188 1.49927 12.4188 1.3326 12.2107L1.0406 11.9186C0.991705 11.8736 0.952675 11.819 0.925976 11.7581C0.899278 11.6973 0.885492 11.6315 0.885492 11.5651C0.885492 11.4986 0.899278 11.4329 0.925976 11.372C0.952675 11.3112 0.991705 11.2566 1.0406 11.2116L5.29393 7.00013L1.0406 2.83138C0.991491 2.78639 0.952276 2.73167 0.925448 2.67069C0.89862 2.60972 0.884766 2.54383 0.884766 2.47721C0.884766 2.41058 0.89862 2.34469 0.925448 2.28372C0.952276 2.22274 0.991491 2.16802 1.0406 2.12303L1.33393 1.83088C1.5006 1.62278 1.83393 1.62278 2.04193 1.83088L6.9606 6.66663C7.04472 6.76805 7.08713 6.89769 7.0792 7.02925C7.07128 7.1608 7.01362 7.28441 6.91793 7.37499Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span className="font-medium text-[16px] leading-[16px] capitalize">
                    {homepageContent?.sustainability?.ctaText || "See More"}
                  </span>
                </motion.button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ESG Cards Section - Responsive Overlapping */}
      <div className="relative -mt-[100px] sm:-mt-[140px] md:-mt-[180px] lg:-mt-[260px] pb-[60px] sm:pb-[80px] lg:pb-[100px] z-30">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-[61px]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 lg:gap-0 lg:flex flex-wrap lg:gap-y-8 lg:justify-center lg:items-end">
            {/* Environment Container */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Link
                href={`/${lang}/sustainability/${homepageContent?.sustainability?.sustainabilities[0]?.slug}`}
                className="flex flex-col items-center gap-4 sm:gap-6 lg:gap-7 w-full lg:w-[439px]"
              >
                <div className="relative w-full lg:w-[439px] h-[250px] sm:h-[300px] lg:h-[371px] bg-neutral-300 overflow-hidden rounded-lg lg:rounded-none group">
                  {homepageContent?.sustainability?.sustainabilities[0]
                    ?.coverImage && (
                    <Image
                      src={getImageUrl(
                        homepageContent?.sustainability?.sustainabilities[0]
                          ?.coverImage?.url || ""
                      )}
                      alt={
                        homepageContent?.sustainability?.sustainabilities[0]
                          ?.coverImage?.name
                      }
                      fill
                      className="object-cover transition-transform duration-300 ease-out group-hover:scale-110"
                      draggable={false}
                    />
                  )}
                </div>
                <h3 className="text-neutral-800 text-center text-lg sm:text-xl lg:text-[24px] font-medium leading-tight lg:leading-[28.8px] tracking-[-0.36px] lg:tracking-[-0.72px]">
                  {homepageContent?.sustainability?.sustainabilities[0]
                    ?.title || ""}
                </h3>
              </Link>
            </motion.div>

            {/* Social Container */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Link
                href={`/${lang}/sustainability/${homepageContent?.sustainability?.sustainabilities[1]?.slug}`}
                className="flex flex-col items-center gap-4 sm:gap-6 lg:gap-7 w-full lg:w-[439px]"
              >
                <div className="relative w-full lg:w-[439px] h-[250px] sm:h-[300px] lg:h-[371px] bg-neutral-300 overflow-hidden rounded-lg lg:rounded-none group">
                  {homepageContent?.sustainability?.sustainabilities[1]
                    ?.coverImage && (
                    <Image
                      src={getImageUrl(
                        homepageContent?.sustainability?.sustainabilities[1]
                          ?.coverImage?.url || ""
                      )}
                      alt={
                        homepageContent?.sustainability?.sustainabilities[1]
                          ?.coverImage?.name
                      }
                      fill
                      className="object-cover transition-transform duration-300 ease-out group-hover:scale-110"
                      draggable={false}
                    />
                  )}
                </div>
                <h3 className="text-neutral-800 text-center text-lg sm:text-xl lg:text-[24px] font-medium leading-tight lg:leading-[28.8px] tracking-[-0.36px] lg:tracking-[-0.72px]">
                  {homepageContent?.sustainability?.sustainabilities[1]
                    ?.title || ""}
                </h3>
              </Link>
            </motion.div>

            {/* Governance Container */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Link
                href={`/${lang}/sustainability/${homepageContent?.sustainability?.sustainabilities[2]?.slug}`}
                className="flex flex-col items-center gap-4 sm:gap-6 lg:gap-7 w-full lg:w-[439px]"
              >
                <div className="relative w-full lg:w-[439px] h-[250px] sm:h-[300px] lg:h-[371px] bg-neutral-300 overflow-hidden rounded-lg lg:rounded-none group">
                  {homepageContent?.sustainability?.sustainabilities[2]
                    ?.coverImage && (
                    <Image
                      src={getImageUrl(
                        homepageContent?.sustainability?.sustainabilities[2]
                          ?.coverImage?.url || ""
                      )}
                      alt={
                        homepageContent?.sustainability?.sustainabilities[2]
                          ?.coverImage?.name
                      }
                      fill
                      className="object-cover transition-transform duration-300 ease-out group-hover:scale-110"
                      draggable={false}
                    />
                  )}
                </div>
                <h3 className="text-neutral-800 text-center text-lg sm:text-xl lg:text-[24px] font-medium leading-tight lg:leading-[28.8px] tracking-[-0.36px] lg:tracking-[-0.72px]">
                  {homepageContent?.sustainability?.sustainabilities[2]
                    ?.title || ""}
                </h3>
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
